import { useState } from 'react';

interface Project {
  title: string;
  category: string;
  description: string;
  outcome: string;
  image: string;
}

export default function Work() {
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);

  const projects: Project[] = [
    {
      title: 'TechFlow Platform',
      category: 'Website Design & Development',
      description: 'A comprehensive SaaS platform for project management',
      outcome: 'Increased user engagement by 240% and reduced onboarding time by 60%',
      image: 'https://public.readdy.ai/ai/img_res/0a231e30b1619ff3d4e66460685f59e3.jpg',
    },
    {
      title: 'Lumina Brand Identity',
      category: 'Brand Design & Strategy',
      description: 'Complete brand system for a sustainable fashion startup',
      outcome: 'Established strong brand recognition and secured Series A funding',
      image: 'https://readdy.ai/api/search-image?query=elegant%20minimalist%20brand%20identity%20design%20showcase%20featuring%20logo%20variations%20business%20cards%20and%20stationery%20on%20white%20marble%20surface%20with%20soft%20natural%20lighting%20and%20premium%20aesthetic%20clean%20composition&width=800&height=600&seq=work2&orientation=landscape',
    },
    {
      title: 'Pulse Fitness App',
      category: 'UI/UX Design',
      description: 'Mobile fitness tracking application with AI coaching',
      outcome: 'Achieved 4.8 star rating with 50K+ downloads in first quarter',
      image: 'https://readdy.ai/api/search-image?query=modern%20mobile%20app%20interface%20design%20mockup%20showing%20fitness%20tracking%20screens%20with%20clean%20white%20interface%20glass%20morphism%20cards%20and%20elegant%20data%20visualization%20on%20smartphone%20display%20premium%20professional%20photography%20style&width=800&height=600&seq=work3&orientation=landscape',
    },
    {
      title: 'Horizon Campaign',
      category: 'Video Production & Social Media',
      description: 'Multi-platform launch campaign for tech product',
      outcome: 'Generated 2M+ impressions and 15% conversion rate',
      image: 'https://readdy.ai/api/search-image?query=professional%20video%20production%20setup%20with%20modern%20camera%20equipment%20and%20lighting%20on%20clean%20white%20background%20showing%20social%20media%20content%20creation%20workspace%20with%20elegant%20minimalist%20aesthetic%20and%20premium%20quality&width=800&height=600&seq=work4&orientation=landscape',
    },
  ];

  return (
    <section id="work" className="relative py-32 px-6 bg-[#F8F9FB]">
      <div className="max-w-7xl mx-auto">
        <h2
          className="text-5xl md:text-6xl font-bold text-black mb-20 leading-tight text-center"
          style={{ fontFamily: 'Inter, sans-serif', letterSpacing: '-0.02em' }}
        >
          Selected Work
        </h2>

        <div className="space-y-12">
          {projects.map((project, index) => (
            <div
              key={index}
              className="group relative backdrop-blur-xl bg-white/60 border border-white/80 rounded-3xl overflow-hidden transition-all duration-500 hover:shadow-2xl cursor-pointer"
              onMouseEnter={() => setHoveredIndex(index)}
              onMouseLeave={() => setHoveredIndex(null)}
            >
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-0">
                <div className="relative h-96 lg:h-auto overflow-hidden">
                  <img
                    src={project.image}
                    alt={project.title}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-r from-black/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                </div>

                <div className="p-12 flex flex-col justify-center">
                  <p
                    className="text-sm font-medium text-black/50 mb-3 uppercase tracking-wider"
                    style={{ fontFamily: 'Inter, sans-serif' }}
                  >
                    {project.category}
                  </p>

                  <h3
                    className="text-4xl font-bold text-black mb-6"
                    style={{ fontFamily: 'Inter, sans-serif', letterSpacing: '-0.02em' }}
                  >
                    {project.title}
                  </h3>

                  <p
                    className="text-lg text-black/70 mb-6 leading-relaxed"
                    style={{ fontFamily: 'Inter, sans-serif' }}
                  >
                    {project.description}
                  </p>

                  <div
                    className="backdrop-blur-xl bg-white/80 border border-white/60 rounded-2xl p-6 transition-all duration-500"
                    style={{
                      opacity: hoveredIndex === index ? 1 : 0.7,
                      transform: hoveredIndex === index ? 'translateY(0)' : 'translateY(10px)',
                    }}
                  >
                    <p
                      className="text-sm font-medium text-black/50 mb-2 uppercase tracking-wider"
                      style={{ fontFamily: 'Inter, sans-serif' }}
                    >
                      Outcome
                    </p>
                    <p
                      className="text-base text-black font-medium"
                      style={{ fontFamily: 'Inter, sans-serif' }}
                    >
                      {project.outcome}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
